import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import {
  AssignAdminsToOwnerBody,
  AssignOwnersToAdminBody,
  RemoveAdminFromOwnerBody,
  RemoveOwnerFromAdminBody,
  UserAssignmentsResponse,
} from "./types";

export const getUserAssignments = createAsyncThunk<
  UserAssignmentsResponse,
  string,
  { rejectValue: string }
>("adminAssignment/getUserAssignments", async (userId, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(`/admin/user/${userId}/assignments`);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(
        error.response?.data?.error?.message || "Failed to load assignments"
      );
    }
    return rejectWithValue("Failed to load assignments");
  }
});

export const assignOwnersToAdmin = createAsyncThunk<
  UserAssignmentsResponse,
  AssignOwnersToAdminBody,
  { rejectValue: string }
>(
  "adminAssignment/assignOwnersToAdmin",
  async ({ adminId, ownerIds }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/admin/user/${adminId}/assign-owners`,
        { ownerIds }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to assign homeowners to admin"
        );
      }
      return rejectWithValue("Failed to assign homeowners to admin");
    }
  }
);

export const removeOwnerFromAdmin = createAsyncThunk<
  UserAssignmentsResponse,
  RemoveOwnerFromAdminBody,
  { rejectValue: string }
>(
  "adminAssignment/removeOwnerFromAdmin",
  async ({ adminId, ownerId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(
        `/admin/user/${adminId}/remove-owner/${ownerId}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to remove homeowner from admin"
        );
      }
      return rejectWithValue("Failed to remove homeowner from admin");
    }
  }
);

export const assignAdminsToOwner = createAsyncThunk<
  UserAssignmentsResponse,
  AssignAdminsToOwnerBody,
  { rejectValue: string }
>(
  "adminAssignment/assignAdminsToOwner",
  async ({ ownerId, adminIds }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/admin/homeowner/${ownerId}/assign-admins`,
        { adminIds }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to assign admins to homeowner"
        );
      }
      return rejectWithValue("Failed to assign admins to homeowner");
    }
  }
);

export const removeAdminFromOwner = createAsyncThunk<
  UserAssignmentsResponse,
  RemoveAdminFromOwnerBody,
  { rejectValue: string }
>(
  "adminAssignment/removeAdminFromOwner",
  async ({ ownerId, adminId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(
        `/admin/homeowner/${ownerId}/remove-admin/${adminId}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to remove admin from homeowner"
        );
      }
      return rejectWithValue("Failed to remove admin from homeowner");
    }
  }
);
